import { BubbleMenu } from "@tiptap/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SlNote } from "react-icons/sl";
import { toast } from "react-toastify";
import { removeAccents } from "../../../../seoAnalyzer/helper";
import { useGetSynonymsMutation } from "../../../../../../redux/slices/postEditorBlogSlice";
import { trackPlatformUsed } from "../../../../../../shared/utils/trackers";

const SynonymBubbleMenu = ({ editor }) => {
  const { t } = useTranslation();
  const [hasSelection, setHasSelection] = useState(false);
  const [word, setWord] = useState();
  const [sentence, setSentence] = useState();
  const [startPoint, setStartPoint] = useState();
  const [endPoint, setEndPoint] = useState();
  const [synonyms, setSynonyms] = useState([]);
  const [getSynonymns, { isLoading: loading }] = useGetSynonymsMutation();
  let selection = false;

  const isContentZone = (node) => {
    if (!node || node?.name === "doc") return false;
    if (node?.name === "contentzone") {
      return true;
    }
    return isContentZone(node.parent);
  };

  const isIntroZone = (node) => {
    if (!node || node?.name === "doc") return false;
    if (node?.name === "introzone") {
      return true;
    }
    return isIntroZone(node.parent);
  };

  const isConclusionZone = (node) => {
    if (!node || node?.name === "doc") return false;
    if (node?.name === "conclusionzone") {
      return true;
    }
    return isConclusionZone(node.parent);
  };

  const handleFindSynonyms = async () => {
    trackPlatformUsed("synonyms", "ai_generation");
    if (word.length > 0 && sentence.length > 0) {
      let payload = {
        nb_outputs: 3,
        word: word,
        sentence: sentence,
      };
      const res = await getSynonymns(payload).unwrap();
      if (res?.status === 200) {
        setSynonyms(res?.data);
      } else {
        toast.error(t("something-is-not-right", "Something is not right"));
      }
    } else {
      toast.error(t("No-word-selected", "No word selected"));
    }
  };

  const handleInsertSynonym = (item) => {
    editor.commands.insertContentAt({ from: startPoint, to: endPoint }, item);
    setSynonyms([]);
  };

  const handleSelectionUpdate = ({ editor }) => {
    const n = editor.$pos(editor.state.selection.head);
    if (isContentZone(n) || isConclusionZone(n) || isIntroZone(n)) {
      const { from, to } = editor.state.selection;

      const $from = editor.state.doc.resolve(from);
      const parent = $from.parent;
      const parentText = parent.textContent;

      const startOffset = from - $from.start();
      const endOffset = to - $from.start();
      let expandedStart = startOffset;
      let expandedEnd = endOffset;
      const originalSelection = parentText.slice(startOffset, endOffset).trim();

      // Expand startOffset to the beginning of the word
      while (expandedStart > 0 && /\w/.test(parentText[expandedStart - 1])) {
        expandedStart--;
      }

      // Expand endOffset to the end of the word
      while (
        expandedEnd < parentText.length &&
        /\w/.test(parentText[expandedEnd])
      ) {
        expandedEnd++;
      }
      const selectedText = parentText.slice(expandedStart, expandedEnd).trim();
      // Convert back to absolute positions
      const absoluteStart = $from.start() + expandedStart;
      const absoluteEnd = $from.start() + expandedEnd;
      setStartPoint(absoluteStart);
      setEndPoint(absoluteEnd);
      // Replace selected (or partial) text with the full word

      const punctuation = /[!"#$%&()*+,./:;<=>?@[\\\]^_`{|}~]/g;
      let containPunctuation = punctuation.exec(removeAccents(selectedText));

      if (
        containPunctuation === null &&
        selectedText.split(" ").length > 0 &&
        selectedText.split(" ").length <= 4 &&
        selectedText.length > 0 &&
        startOffset != endOffset &&
        parent?.type?.name != "heading" &&
        originalSelection.length > 0
      ) {
        setSynonyms([]);
        // Use regex to split text into "sentences" by punctuation or newline
        const sentenceRegex = /[^.!?\n]*[.!?\n]+|[^.!?\n]+$/g;

        let fullSentence = "";
        let sentenceStart = 0;
        let sentenceEnd = 0;

        let match;
        while ((match = sentenceRegex.exec(parentText)) !== null) {
          const start = match.index;
          const end = start + match[0].length;

          if (startOffset >= start && endOffset <= end) {
            fullSentence = match[0].trim();
            sentenceStart = start;
            sentenceEnd = end;
            break;
          }
        }
        setHasSelection(true);
        selection = true;
        const selectionRelativeStart = startOffset - sentenceStart;
        const selectionRelativeEnd = endOffset - sentenceStart;

        const textBefore = fullSentence.slice(0, selectionRelativeStart);
        const textAfter = fullSentence.slice(selectionRelativeEnd);
        setWord(selectedText);
        setSentence(fullSentence);
      } else {
        setHasSelection(false);
        setWord();
        setSentence();
        selection = false;
      }
    }
  };

  if (editor) {
    editor.on("selectionUpdate", handleSelectionUpdate);
  }

  return (
    <div>
      <BubbleMenu
        editor={editor}
        tippyOptions={{
          duration: 100,
          // placement: "bottom", // Choose 'top', 'bottom', 'left', or 'right'
          strategy: "fixed", // Use fixed positioning
          offset: [0, 20], // Adjust the offset as needed
        }}
        shouldShow={({ editor, state }) => {
          const n = editor.$pos(state.selection.head);

          return (
            (isContentZone(n) || isConclusionZone(n) || isIntroZone(n)) &&
            selection &&
            synonyms.length == 0
          );
          // && n.name === 'heading';
        }}
      >
        <div className="text-sm min-w-[200px] overflow-x-hidden overflow-y-auto flex flex-col bg-neutral-500 text-white rounded-md">
          {hasSelection &&
            synonyms.length == 0 &&
            (!loading ? (
              <button
                disabled={loading}
                className="p-2 hover:bg-neutral-600 flex items-center gap-4"
                onClick={handleFindSynonyms}
              >
                <SlNote />

                {t("Find-Synonyms", "Find Synonyms")}
              </button>
            ) : (
              <div
                role="status"
                className="p-2 flex items-center justify-center"
              >
                <svg
                  aria-hidden="true"
                  class="w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span class="sr-only">Loading...</span>
              </div>
            ))}
          {synonyms.length > 0 && (
            <div className=" w-full max-w-[200px] break-words p-2">
              {synonyms.map((item) => {
                return (
                  <div
                    className="bg-white rounded-xl cursor-pointer text-black px-2 py-1 my-2  text-xs"
                    onClick={() => {
                      handleInsertSynonym(item);
                    }}
                  >
                    {item}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </BubbleMenu>
    </div>
  );
};

export default SynonymBubbleMenu;
